<script setup>
import { useProductsByCodes } from '~/composables/useProductsByCode'
import { usePageSeo } from '~/composables/usePageSeo'
import { productCodes } from '~/composables/productCodes'

usePageSeo({
  title: 'Enterprise Email - Secure Email - Register Enterprise Email - NicSRS',
  description:
    'Highly available and scalable, NicSRS enterprise email supports all mainstream email clients and ensures seamless global email communication.',
  bodyId: 'email-page'
})

const { data: popularProducts } = await useProductsByCodes(productCodes.email)
</script>

<template>
  <div>
    <div class="ssl-banner">
      <div class="inner-bound">
        <h1>NicSRS Secured Enterprise Email</h1>
        <p>
          With more than ten years of experience in enterprise email service,
          <a
            href="https://www.ssltrust.com/smime-cert"
            target="_blank"
            rel="noopener noreferrer"
            style="color: var(--theme-color)"
            >sslTrus email system</a
          >
          has summed up its own set of solutions which are provided free of charge to users.
        </p>
      </div>
      <img
        src="/static/images/banner/racent-email-hosting-service.jpg"
        alt="NicSRS Secured Enterprise Email"
        class="isPC"
      />
      <img
        src="/static/images/banner/racent-email-hosting-service-phone.jpg"
        alt="NicSRS Secured Enterprise Email"
        class="isPhone"
      />
    </div>

    <div class="email-service">
      <div class="inner-bound email-service-box">
        <div>
          <h4>sslTrus secured email free support</h4>
          <p>Deploy HTTPS secure access for email domains</p>
        </div>
        <div>
          <h4>Global deliverability</h4>
          <p>Only the optimal line, ensure email deliverability worldwide</p>
        </div>
        <div>
          <h4>Email migration</h4>
          <p>Migrate emails from the original email service provider to the new mailbox, simple and convenient.</p>
        </div>
      </div>
    </div>

    <div class="inner-bound padding2-60">
      <h2>Find the Right Plan for Your Business</h2>
      <p class="whmcs-p">
        NicSRS provides email products with different features and deployment methods, up to 50% off.
      </p>
      <div class="popular-cert">
        <div>
          <div class="popular-cert-box">
            <div class="popular-cert-title">NicSRS Enterprise Email – Flagship</div>
            <p class="dns-package">Designed for SMEs, quickly build brand image</p>
            <a
              href="https://console.nicsrs.com/product/config/cndns-mail-enterprise"
              target="_blank"
              rel="noopener noreferrer"
              >BUY NOW</a
            >
          </div>
          <div class="popular-cert-info">
            <p>Custom enterprise image, exclusive domain, logo</p>
            <p>Protect enterprise information security, 24/7 anti-virus monitoring</p>
            <p>5 email accounts by default</p>
            <p>Login from multiple devices</p>
            <p>More efficient collaboration, general management</p>
          </div>
        </div>
        <div v-for="product in popularProducts" :key="product.code">
          <div class="popular-cert-box">
            <div class="popular-cert-title">
              <NuxtLink :to="`/product/${product.code}`">{{ product.name }}</NuxtLink>
            </div>
            <p class="dns-package">{{ product.abbr_description }}</p>
            <div class="popular-cert-price">
              <div>
                <p>${{ Number(product.price012).toFixed(2) }}/yr</p>
                <p>${{ Number(product.listprice).toFixed(2) }}/yr</p>
              </div>
              <p>{{ Math.round((1 - product.price012 / product.listprice) * 100) }}% Off</p>
            </div>
            <a
              :href="`https://console.nicsrs.com/product/config/${product.code}`"
              target="_blank"
              rel="noopener noreferrer"
              >BUY NOW</a
            >
          </div>
          <div v-if="product.spec" class="popular-cert-info"><p v-html="product.spec"></p></div>
        </div>
      </div>
    </div>

    <div class="bound padding2-60">
      <div class="inner-bound">
        <h2>8 Advantages of NicSRS Enterprise Email</h2>
        <div class="three-noimg-box four-box">
          <div>
            <h4>Global Deliverability</h4>
            <p>Independent nodes in Shanghai, Hong Kong, Beijing, the US and more.</p>
          </div>
          <div>
            <h4>Enterprise Cloud</h4>
            <p>Make uploading, downloading and sharing much easier.</p>
          </div>
          <div>
            <h4>Stable System</h4>
            <p>All email servers are cloud-based and adopt a high availability and scalability architecture.</p>
          </div>
          <div>
            <h4>Secure and Reliable</h4>
            <p>
              Through behavioral analysis of senders, help customers avoid spam, virus, phishing email and security
              risks.
            </p>
          </div>
          <div>
            <h4>Data Backup</h4>
            <p>
              Multi-tiered and multi-copy storage, remote real-time backup of key data, accidental deletion recovery
              within 7 days.
            </p>
          </div>
          <div>
            <h4>Attachment Online Preview</h4>
            <p>Better user experience without having to download.</p>
          </div>
          <div>
            <h4>Dedicated IPs Supported</h4>
            <p>NicSRS enterprise email allows you to send unlimited emails.</p>
          </div>
          <div>
            <h4>Scan to Log In</h4>
            <p>Scan QR code to log in after adding the email account.</p>
          </div>
        </div>
      </div>
    </div>

    <div class="inner-bound padding-60">
      <h2>Enterprise Email Features</h2>
      <div class="email-function">
        <div>
          <div class="function-head">Product features</div>
          <div class="function-content">
            <div class="function-content-head">
              <div>
                <img src="/static/images/ssl/session-mode.png" alt="Session mode" />
                <h4>Session mode</h4>
                <p>Browsing emails by subjects, correlating a set of emails exchanged in one session.</p>
              </div>
              <div>
                <img src="/static/images/ssl/mobile-mail.png" alt="Stay mobile" />
                <h4>Stay mobile</h4>
                <p>Create and manage your emails wherever and whenever, quickly set up iOS and Android.</p>
              </div>
              <div>
                <img src="/static/images/ssl/address-book-management.png" alt="Contacts management" />
                <h4>Contacts management</h4>
                <p>Divided into domain contacts and personal contacts for easy management.</p>
              </div>
            </div>
            <div class="function-content-body">
              <div>
                <i></i>
                <div>
                  <h4>Anti-spam settings</h4>
                  <p>Add anti-spam rules and sender whitelist and blacklist.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>IP authorized login</h4>
                  <p>Set default IP so that your email can't be logged in from other IP addresses.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Domain aliases for email client</h4>
                  <p>Domain alias login supports pop, imap, and smtp methods.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Operation verification code</h4>
                  <p>Verification code will be sent whenever there's an operation of deleting or changing user.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Search all</h4>
                  <p>Fast email search based on email content or sender/recipient.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Attachments</h4>
                  <p>General attachments of up to 70MB and large attachments up to 2G.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Same domain aliases</h4>
                  <p>Same domain alias login supports pop, imap, and smtp methods.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Mass emails, hide recipients</h4>
                  <p>Make your emails feel more personalized and less spammy.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Email business card</h4>
                  <p>Set sender signatures at the end of each email.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Multi-lingual interface</h4>
                  <p>English, Chinese (traditional) and Chinese (simplified) supported.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Email online migration</h4>
                  <p>The admin is allowed to execute user email migration under the domain.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Email sync</h4>
                  <p>Synchronize email messages across multiple email clients.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="function-head">Product features</div>
          <div class="function-content">
            <div class="function-content-head">
              <div>
                <img src="/static/images/ssl/moving.png" alt="Migration" />
                <h4>Migration</h4>
                <p>Easy to migrate the email messages in the original enterprise email to the new email.</p>
              </div>
              <div>
                <img src="/static/images/ssl/department-management.png" alt="Department management" />
                <h4>Department management</h4>
                <p>Create email addresses for different departments to realize departmental customized management.</p>
              </div>
              <div>
                <img src="/static/images/ssl/email-monitoring.png" alt="Email monitoring" />
                <h4>Email monitoring</h4>
                <p>Set monitoring accounts and monitored accounts to strengthen email management.</p>
              </div>
            </div>
            <div class="function-content-body">
              <div>
                <i></i>
                <div>
                  <h4>Custom settings</h4>
                  <p>Change logo, icon and company profile.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Blacklist and whitelist</h4>
                  <p>The blacklist is used to reject emails. The whitelist will not enable anti-spam.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Log analysis</h4>
                  <p>Each email login and each admin operation are logged.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>IP authorized login</h4>
                  <p>Sending and receiving emails from IP addresses in the blacklist are impossible.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Mobile supported</h4>
                  <p>Responsive to mobile web browsers.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Mailbox management</h4>
                  <p>Enable, disable or modify a mailbox, set sending or receiving limits.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Applet supported</h4>
                  <p>Log in from the email applet.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Retrieve deleted emails</h4>
                  <p>Deleted emails can be retrieved within 7 days.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Assign administrators</h4>
                  <p>Assign multiple sub-administrators with managed mailboxes and restricted operations.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Alias domain management</h4>
                  <p>Up to 200 aliases and email domain change free of charge.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Set send time</h4>
                  <p>Set send time in advance and the system will send your email at the set time.</p>
                </div>
              </div>
              <div>
                <i></i>
                <div>
                  <h4>Third-party LDAP authentication</h4>
                  <p>Supports integration with AD using LDAP for internal email services.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bound padding-60">
      <div class="inner-bound">
        <h2>Enterprise Email Q&amp;A</h2>
        <div class="faq">
          <h4>
            <span>Q</span>Can I migrate to NicSRS email if I'm already using the enterprise email of another service
            provider?
          </h4>
          <div class="faq-info">
            <span>A</span>
            <p>
              Yes, NicSRS enterprise email allows email migration. You can migrate your email data by setting up your
              old email server and email protocol.
            </p>
          </div>
        </div>
        <div class="faq">
          <h4>
            <span>Q</span>I already own a domain registered with another registrar, can I use this domain in NicSRS
            email?
          </h4>
          <div class="faq-info">
            <span>A</span>
            <p>
              Yes. We recommend that you buy domains from NicSRS, but you're able to use and set up email with domain
              names from other providers.
            </p>
          </div>
        </div>
        <div class="faq">
          <h4><span>Q</span>How to determine the number of email accounts and validity for my company?</h4>
          <div class="faq-info">
            <span>A</span>
            <p>
              Please determine the number of email accounts needed based on the number of employees. The more accounts
              you purchase, the lower the unit price will be. Considering business growth, it's recommended to buy 20%
              more accounts.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
